import React from 'react';
import { cn } from '@utils/classNames';
import { SidebarTooltip } from './SidebarTooltip';

interface SidebarNavButtonProps {
  label: string;
  isActive?: boolean;
  collapsed?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  icon: React.ReactNode;
  className?: string;
  variant?: 'nav' | 'action' | 'danger';
}

export const SidebarNavButton: React.FC<SidebarNavButtonProps> = ({
  label,
  isActive = false,
  collapsed = false,
  onClick,
  onMouseEnter,
  icon,
  className,
  variant = 'nav',
}) => {
  const baseClasses = cn(
    'w-full flex items-center rounded-xl transition-all touch-manipulation',
    collapsed ? 'justify-center h-10 px-0' : 'gap-3 h-[42px] px-4',
    variant === 'nav' &&
      (isActive
        ? 'bg-white/12 text-white'
        : 'opacity-80 hover:opacity-100 hover:bg-white/[0.06]'),
    variant === 'action' &&
      'bg-white/5 text-white/70 hover:text-white hover:bg-white/10',
    variant === 'danger' &&
      'bg-white/5 border border-white/10 hover:bg-danger/10 hover:border-danger/30',
    className
  );

  const content = (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={baseClasses}
      aria-label={label}
      title={collapsed ? undefined : label}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center',
          isActive ? 'text-white' : 'text-white opacity-80',
          variant === 'danger' && 'text-danger-light'
        )}
      >
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
              className: cn('w-5 h-5', (icon as React.ReactElement).props?.className),
            })
          : icon}
      </div>
      {!collapsed && (
        <span
          className={cn(
            'font-codec font-bold text-base truncate',
            isActive ? 'text-white' : 'text-white opacity-80',
            variant === 'danger' && 'text-danger-light text-sm',
            variant === 'action' && 'font-medium text-sm'
          )}
        >
          {label}
        </span>
      )}
    </button>
  );

  if (collapsed) {
    return <SidebarTooltip label={label}>{content}</SidebarTooltip>;
  }

  return content;
};
